import React from 'react';
import { useTranslation } from 'next-i18next';
import { toDe } from '@/utilites/sku-operations';
import { SkuMainImage } from '@/utilites/get-sku-main-image';
import imageLoader from '@/utilites/imageLoader';
import localePath from '@/utilites/language/locale-path';
import SkuCardDescription from './SkuCardDescription';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import { useRouter } from 'next/router';
import useEvents from '@/hooks/useEvents';
import { useAnalytics } from '@/hooks/useAnalytics';
import { SkuDTO } from '@/types/api/api-v2';
import useBackendUser from '@/hooks/useBackendUser';

type SkuCardSellerModalContentProps = {
  sku: SkuDTO;
  askValue: number;
  sellerName: string | number;
  askId?: string;
};

export default function SkuCardSellerModalContent({
  sku,
  askValue,
  sellerName,
  askId,
}: SkuCardSellerModalContentProps) {
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const { fireBuyNowButtonClickEvent } = useEvents('', []);
  const { getPageType } = useAnalytics();
  const { data: user } = useBackendUser();
  // Logged in: the pay-first checkout for exactly this ask. Guests keep the guest
  // checkout on the buy page.
  const buyHref = user
    ? `/${sku.slug}/checkout?ask_id=${askId || ''}`
    : `/${sku.slug}/buy?buy=true&ask_id=${askId || ''}`;

  const handleBuyNow = () => {
    // Fire analytics event
    fireBuyNowButtonClickEvent({
      slug: sku.slug,
      product_name: sku.name || '',
      product_category: sku?.category?.name || '',
      product_price: askValue,
      page_type: getPageType(),
    });

    router.push(buyHref);
  };

  return (
    // Make modal content scrollable if it overflows
    <div className="tw-max-h-[80vh] tw-overflow-y-auto">
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
        {/* Product Image */}
        <div className="tw-flex tw-aspect-square tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-p-8 md:tw-aspect-auto md:tw-h-full md:tw-max-h-[40vh]">
          <SkuMainImage
            width={300}
            height={300}
            alt={sku?.name ?? ''}
            loader={imageLoader}
            className="tw-h-full tw-w-full tw-bg-white tw-object-contain"
            src={sku.mainImageUrl as string}
          />
        </div>

        {/* Product Details */}
        <div className="tw-flex tw-flex-col tw-gap-3">
          {/* Product Name */}
          <div>
            <h3 className="tw-mb-2 tw-text-xl tw-font-bold tw-text-gray-900">{sku?.name}</h3>
            <div className="tw-text-sm tw-text-gray-600">
              <SkuCardDescription sku={sku} />
            </div>
          </div>

          {/* Seller Information */}
          {sellerName && (
            <div className="tw-flex tw-items-center tw-gap-2">
              <h4 className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-gray-900">
                {tCommon('seller')}:
              </h4>
              <p className="tw-mb-0 tw-text-gray-700">{sellerName}</p>
            </div>
          )}

          {/* Price Information */}
          <div className="tw-rounded-lg tw-bg-blue-50 tw-p-3">
            <h4 className="tw-mb-2 tw-font-semibold tw-text-gray-900">{tCommon('seller_ask')}</h4>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-2xl tw-font-bold tw-text-blue-600">
                {askValue ? toDe(askValue) : '--'}
              </span>
            </div>
          </div>

          {/* Comprehensive Product Specifications */}
          <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-3">
            <h4 className="tw-text-md tw-mb-2 tw-font-semibold tw-text-gray-900">
              {tCommon('product_details')}
            </h4>
            <div className="tw-grid tw-grid-cols-1 tw-gap-2 tw-text-sm">
              {sku?.bottleSizeInMl && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('bottle_size')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">{sku.bottleSizeInMl}ml</span>
                </div>
              )}
              {sku?.volumeInPercent && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('alcohol_content')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">{sku.volumeInPercent / 10}%</span>
                </div>
              )}
              {sku?.brand?.name && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('brand')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">{sku.brand.name}</span>
                </div>
              )}
              {sku?.category?.name && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('category')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">{sku.category.name}</span>
                </div>
              )}
              {sku?.distilledYear && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('distilled_year')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">{sku.distilledYear}</span>
                </div>
              )}
              {sku?.brand?.countryOfOrigin && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('country_of_origin')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">
                    {sku.brand.countryOfOrigin}
                  </span>
                </div>
              )}
              {sku?.brand?.region && (
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-nowrap tw-font-medium tw-text-gray-700">
                    {tCommon('region')}:
                  </span>
                  <hr className="tw-m-0 tw-mb-1.5 tw-w-full tw-border-2 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-dashed tw-border-[#5F6D7E66] tw-p-0 tw-opacity-100"></hr>
                  <span className="tw-text-nowrap tw-text-gray-600">{sku.brand.region}</span>
                </div>
              )}
              {sku?.description && (
                <div className="tw-col-span-2 tw-mt-3">
                  <span className="tw-mb-1 tw-block tw-font-medium tw-text-gray-700">
                    {tCommon('description')}:
                  </span>
                  <p className="tw-text-sm tw-text-gray-600">{sku.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Button */}
          <div className="tw-mt-4">
            <PrimaryButton
              onClick={handleBuyNow}
              className="tw-w-full tw-bg-blue-600 hover:tw-bg-blue-700"
            >
              <a
                href={localePath(buyHref, router.locale)}
                className="tw-block tw-h-full tw-w-full tw-text-white tw-no-underline"
                tabIndex={-1} // Prevent tab focus on the <a> since the button is the focusable element
                onClick={e => e.preventDefault()} // Prevent default link navigation
              >
                {tCommon('buy_now')} - {askValue ? toDe(askValue) : '--'}
              </a>
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
